import { filter, isEmpty, map, orderBy } from "lodash";
import moment from "moment";
import {
  AICC_LANGUAGE_STATUS,
  AICC_VERSION_EVENT_ACTION,
  AiccAssetStatus,
  AiccAssetType,
  AiccLanguageStatus,
  AiccVersionEventAction,
  AiccVersionScope,
  AiccVersionStatus,
  AiccWorkingCopyOrigin,
} from "constants/aiccLanguage";
import {
  AiccAvailableLanguageResponse,
  AiccLanguageAssetResponse,
  AiccLanguageContentVersionResponse,
  AiccLanguageContentVersionsResponse,
  AiccLanguageListItemResponse,
  AiccLanguageListResponse,
  AiccLanguageVersionResponse,
} from "../dto/response/aiccLanguage";
import {
  AiccAvailableLanguage,
  AiccLanguageAsset,
  AiccLanguageContentVersion,
  AiccLanguageVersion,
  AiccLanguageVersionEvent,
  AiccManagedLanguage,
  AiccManagedLanguageList,
} from "../model/aiccLanguage";

export function transformAiccLanguageAsset(response: AiccLanguageAssetResponse): AiccLanguageAsset {
  return {
    assetType: response.asset_type as AiccAssetType,
    status: response.status as AiccAssetStatus,
    summary: response.summary ?? "",
    detailLines: response.detail_lines ?? [],
    // Absent means required: the ticket treats every listed asset as gating unless
    // the backend explicitly says otherwise.
    isRequired: response.is_required ?? true,
  };
}

/**
 * The three pairs read as the sequence they summarise, for a backend that predates `events`.
 *
 * Only what those fields can actually witness: one mint, one withdrawal, one republish. It is lossy by
 * construction — that loss is the reason `events` exists — but it reproduces exactly what the drawer
 * rendered before, which is what makes the two deploy orders equivalent.
 */
function eventsFromLegacyFields(response: AiccLanguageVersionResponse): AiccLanguageVersionEvent[] {
  const acts: [AiccVersionEventAction, string | undefined, string | undefined][] = [
    [AICC_VERSION_EVENT_ACTION.published, response.published_by, response.published_at],
    [AICC_VERSION_EVENT_ACTION.withdrawn, response.withdrawn_by, response.withdrawn_at],
    [AICC_VERSION_EVENT_ACTION.republished, response.republished_by, response.republished_at],
  ];

  // Either half is enough, the same rule the drawer used: an act with no operator named is still an act.
  return map(
    filter(acts, ([, by, at]) => !!by || !!at),
    ([action, by, at]) => ({ action, by: by ?? "", at: at ?? "", notes: "" })
  );
}

/**
 * 🔴 SORTED HERE, BECAUSE THE RESPONSE IS NOT SORTED. Dev returns Albanian V3's republish (08:12:40) ahead
 * of a withdrawal at 08:11:12. The trail's whole value is reading in the order the acts happened, so the
 * order is established once, at the boundary, rather than trusted or re-derived per consumer.
 *
 * `moment`, not the raw string: these are ISO 8601 WITH an offset, and comparing them as text is only
 * correct while every row carries the same offset. They all say `+00:00` today; that is not a contract.
 */
function transformAiccLanguageVersionEvents(response: AiccLanguageVersionResponse): AiccLanguageVersionEvent[] {
  // 🔴 `[]` FALLS THROUGH TO THE LEGACY FIELDS, and must. Migrated versions come back with no events and a
  // populated `published_by` of `"migration"` — taking the empty array at face value blanks their trail.
  if (isEmpty(response.events)) {
    return eventsFromLegacyFields(response);
  }

  return orderBy(
    map(response.events, (event) => ({
      action: event.action as AiccVersionEventAction,
      by: event.by ?? "",
      at: event.at ?? "",
      notes: event.notes ?? "",
    })),
    (event) => moment(event.at).valueOf()
  );
}

export function transformAiccLanguageVersion(response: AiccLanguageVersionResponse): AiccLanguageVersion {
  return {
    version: response.version,
    status: response.status as AiccVersionStatus,
    releaseNotes: response.release_notes ?? "",
    publishedBy: response.published_by ?? "",
    publishedAt: response.published_at ?? "",
    withdrawnBy: response.withdrawn_by ?? "",
    withdrawnAt: response.withdrawn_at ?? "",
    republishedBy: response.republished_by ?? "",
    republishedAt: response.republished_at ?? "",
    events: transformAiccLanguageVersionEvents(response),
  };
}

export function transformAiccLanguageContentVersion(
  response: AiccLanguageContentVersionResponse
): AiccLanguageContentVersion {
  return {
    scope: response.version_scope as AiccVersionScope,
    // Absent rather than null, so callers cannot accidentally print "V null" for a working copy.
    versionNumber: response.version_number ?? undefined,
    // The backend's own answer. Deriving it from the scope would let a button be enabled where
    // the write is refused.
    isEditable: response.editable ?? false,
    assets: map(response.assets, transformAiccLanguageAsset),
  };
}

export function transformAiccManagedLanguage(response: AiccLanguageListItemResponse): AiccManagedLanguage {
  return {
    id: response.id,
    name: response.name,
    status: (response.status ?? AICC_LANGUAGE_STATUS.draft) as AiccLanguageStatus,
    isPublic: response.is_public ?? false,
    isDeleted: response.is_deleted ?? false,
    hasWorkingCopy: response.has_working_copy ?? false,
    // 🔴 `undefined`, never `?? false`. Only the three copy endpoints answer these; a list row, a
    // `/managed` read or the refresh after a publish carries neither. Defaulting to `false` would make
    // such a row claim "no copy was created and this was not a restore" — true but meaningless there,
    // and any wording keyed off it would fire on a response that never answered the question.
    // `undefined` means "this response is not about a copy action".
    workingCopyCreated: response.working_copy_created ?? undefined,
    restoredFromArchive: response.restored_from_archive ?? undefined,
    // Absent rather than empty: there is nothing to say about the origin of a copy that is not there.
    workingCopyOrigin: (response.working_copy_origin || undefined) as AiccWorkingCopyOrigin | undefined,
    workingCopySource: response.working_copy_source ?? undefined,
    // `public_version` is what dev actually sends; see the alias's note in the response type.
    liveVersion: response.public_version ?? response.live_version ?? undefined,
    nextVersion: response.next_version ?? undefined,
    versions: map(response.versions, transformAiccLanguageVersion),
    // Absent stays absent: `null` means this response was asked not to compute the copies, while
    // `[]` means the language has none. Collapsing both into `[]` would make a normal language look
    // like one with no content at all.
    contentVersions: response.content_versions
      ? map(response.content_versions, transformAiccLanguageContentVersion)
      : undefined,
  };
}

/** The readable copies of one language, from the endpoint that exists to report only those. */
export function transformAiccLanguageContentVersions(
  response: AiccLanguageContentVersionsResponse
): AiccLanguageContentVersion[] {
  return map(response?.versions, transformAiccLanguageContentVersion);
}

export function transformAiccManagedLanguageList(response: AiccLanguageListResponse): AiccManagedLanguageList {
  return {
    items: map(response?.items, transformAiccManagedLanguage),
    total: response?.total ?? 0,
  };
}

export function transformAiccAvailableLanguage(response: AiccAvailableLanguageResponse): AiccAvailableLanguage {
  return {
    id: response.id,
    name: response.name,
  };
}
